import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Moon, Waves } from 'lucide-react';
import CTA from '../../components/CTA';

const BangorAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Bangor | Coastal Stress Relief North Down</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Bangor, North Down from a south Belfast clinic on the Ormeau Road. Retirement anxiety, empty nest, professional burnout. CNHC registered coastal wellness approach."
        />
        <meta name="keywords" content="anxiety treatment Bangor, stress management North Down, hypnotherapy Bangor anxiety, retirement anxiety Bangor, therapist Bangor stress, coastal wellness" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bangor-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Bangor | Coastal Stress Relief North Down" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Bangor. Specialized support for retirement anxiety, empty nest syndrome, professional burnout, coastal living wellness." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Bangor",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Bangor and North Down from Belfast clinic. Specializing in retirement anxiety, empty nest syndrome, professional burnout, and coastal wellness approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/bangor-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Bangor",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Down"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Belfast Wellness Clinic, Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Bangor for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre and roughly half an hour's drive from Bangor. Bangor is on the Belfast-Bangor rail line, so the journey is easy without a car too - Metro bus services run along the Ormeau Road corridor from the city centre. For Bangor residents, specialist anxiety treatment is very accessible."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with retirement anxiety and life transitions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Retirement anxiety is one of the most common issues I treat among Bangor and North Down clients. The transition from busy professional life to retirement can trigger feelings of loss of purpose, identity shifts, health anxiety, and social isolation. Solution-focused hypnotherapy helps by processing the emotional adjustment to retirement, building new sources of meaning and purpose, reducing worry about aging and health, improving sleep quality often disrupted by anxiety, and creating positive expectations for this new chapter of life. Most clients see significant improvement within 4-6 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "Do you work with empty nest syndrome?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, empty nest syndrome - the grief and anxiety when adult children leave home - is particularly common in affluent areas like Bangor where parents have been very involved in their children's lives. Hypnotherapy helps process the grief of this life transition, rebuild identity beyond 'parent' role, reduce anxiety about your children's independence, improve relationship with your partner after the focus shifts from parenting, and create excitement for the freedoms this new phase brings. The approach is compassionate, evidence-based, and typically takes 4-6 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy complement a coastal wellness lifestyle?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Bangor's coastal location naturally supports wellness - sea air, beach walks, sailing, marine environment - and hypnotherapy fits perfectly into this holistic approach to health. While coastal living reduces some stress, it doesn't eliminate anxiety from work, life transitions, or health concerns. Hypnotherapy provides the mental wellness component to complement your physical wellness practices, creating a comprehensive approach to wellbeing."
                }
              },
              {
                "@type": "Question",
                "name": "Is professional burnout treatable with hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, professional burnout - characterized by exhaustion, cynicism about work, and reduced effectiveness - responds very well to hypnotherapy. Many Bangor professionals commuting to Belfast experience burnout from years of high-pressure work. Solution-focused hypnotherapy helps by reducing your overall stress baseline so you recover faster, improving sleep quality to restore energy, teaching relaxation techniques to prevent future burnout, and helping you establish healthier work-life boundaries. Most clients notice significant improvement within 4-6 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "What's your approach to health anxiety in older adults?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Health anxiety - excessive worry about developing serious illness - is particularly common in the 50+ age group and can become consuming. My medical background as a nurse helps me understand the genuine health concerns of aging while addressing the excessive anxiety that prevents you from enjoying life. Hypnotherapy doesn't dismiss your health concerns as 'all in your head' but helps you respond to them proportionately rather than catastrophically. We work on reducing the constant vigilance for symptoms, managing the anxiety when you do have medical appointments, and improving your quality of life despite normal age-related health changes."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety & Stress Relief for Bangor
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Bangor & North Down from our Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">South Belfast clinic</span> on the Ormeau Road • <span className="font-semibold">around 30 min drive</span> from Bangor
                </p>
              </div>
              <p className="text-gray-600 mt-2">Frequent trains on the Belfast-Bangor line • Online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Anxiety Symptoms Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Do You Recognize These Signs of Anxiety?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-primary-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Brain className="w-6 h-6 text-accent-600 mr-2" />
                  Mental Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Constant worry about health, aging, or life purpose</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Racing thoughts that won't switch off at bedtime</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Feeling lost or anxious after retirement or empty nest</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Perfectionism and overachievement leading to exhaustion</span>
                  </li>
                </ul>
              </div>
              <div className="bg-accent-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Heart className="w-6 h-6 text-primary-600 mr-2" />
                  Physical Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tight chest, rapid heartbeat, or palpitations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tense muscles, jaw clenching, or tension headaches</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Sleep problems (can't fall asleep or wake at 3am worrying)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Digestive issues or nausea from chronic stress</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-center">
                <Waves className="w-6 h-6 text-blue-600 mr-2" />
                Life Transitions in Coastal Living
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Bangor's affluent, health-conscious community faces unique anxiety triggers: retirement anxiety after high-achieving professional careers, empty nest syndrome as adult children leave the family home, health anxiety and aging concerns particularly common in the 50+ demographic, professional burnout from years of Belfast commuting and demanding careers, and pressure to maintain the "coastal wellness" lifestyle even when struggling mentally. These aren't personal failings - they're normal responses to major life transitions that hypnotherapy can help you navigate successfully.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Relieves Anxiety & Stress
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8 text-center">
              Anxiety happens when your brain's threat detection system (the amygdala) becomes overactive. Solution-focused hypnotherapy retrains this response at a subconscious level.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Calm the Nervous System</h3>
                <p className="text-gray-700 leading-relaxed">
                  Hypnosis directly accesses the subconscious mind where anxiety patterns are stored, helping to reduce your brain's overactive threat response and restore calm.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mb-4">
                  <Moon className="w-6 h-6 text-accent-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Restore Sleep Quality</h3>
                <p className="text-gray-700 leading-relaxed">
                  Anxiety disrupts sleep, which worsens anxiety - a vicious cycle. Hypnotherapy teaches your brain new relaxation responses, restoring restorative sleep naturally.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Navigate Transitions</h3>
                <p className="text-gray-700 leading-relaxed">
                  Life transitions (retirement, empty nest, aging) are inherently stressful. Hypnotherapy builds your capacity to adapt and find meaning in new life chapters.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="font-bold text-xl text-gray-900 mb-4 text-center">What Happens in a Session?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 1: Understanding (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We discuss what's been happening with your anxiety, what triggers it, and what you want to achieve. I explain the neuroscience of anxiety in simple terms so you understand why you feel this way and how we'll address it. My 15 years as a nurse means I understand the medical perspective on anxiety too.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 2: Hypnosis (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You'll sit comfortably while I guide you into deep relaxation (similar to meditation or the feeling just before sleep). In this relaxed state, your subconscious mind is receptive to positive suggestions for calm, confidence, and resilience. You remain aware and in control throughout - this is not stage hypnosis.
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4 text-center">
                Most clients see improvement within 4-6 sessions, with each session building on the last to create lasting change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Bangor
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-primary-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Car className="w-6 h-6 text-accent-600 mr-2" />
                  By Car (around half an hour)
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>From Bangor:</strong> Head into Belfast, then out along the Ormeau Road (A24) into the south of the city</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Destination:</strong> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ - about 10 minutes from the city centre</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Journey time:</strong> Roughly half an hour, depending on city traffic (a familiar route for Bangor commuters)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-accent-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Train className="w-6 h-6 text-primary-600 mr-2" />
                  By Train
                </h3>
                <ul className="space-y-3 text-gray-700 mb-4">
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Service:</strong> Bangor sits on the Belfast-Bangor line, with frequent Translink trains into central Belfast</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Journey:</strong> A direct run into the city with trains throughout the day</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Onward to the clinic:</strong> Metro bus services run along the Ormeau Road corridor into south Belfast, or it's a short taxi from the city centre</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 italic">
                  Perfect for those who prefer not to drive or already commute by train to Belfast for work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Bangor and North Down Clients Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "After retiring from a demanding career in finance, I felt completely lost and anxious about the future. Sharon's approach helped me process that transition and find new purpose. Six months on, I'm genuinely excited about this new chapter."
                </p>
                <p className="font-semibold text-gray-900">J.R., Bangor</p>
                <p className="text-sm text-gray-600">Retirement anxiety & identity</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "Empty nest hit me harder than I expected. When my youngest left for university, I was having panic attacks. Sharon's hypnotherapy helped me grieve that loss while also embracing the freedom. The train journey from Bangor to Belfast was easy and worth it."
                </p>
                <p className="font-semibold text-gray-900">P.M., Bangor West</p>
                <p className="text-sm text-gray-600">Empty nest syndrome & panic</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "Years of commuting to Belfast for work had led to complete burnout. I couldn't sleep, was irritable with my family, and felt exhausted all the time. After 5 sessions with Sharon, I felt like myself again. Her medical background meant she really understood the physiology of burnout."
                </p>
                <p className="font-semibold text-gray-900">D.K., Belfast</p>
                <p className="text-sm text-gray-600">Professional burnout & insomnia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Bangor Clients Choose Sharon Clare Hypnotherapy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Medical Background</h3>
                  <p className="text-gray-700 leading-relaxed">
                    15 years as a nurse at Belfast City Hospital means I understand anxiety from both a medical and therapeutic perspective - particularly important for health anxiety common in older adults.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Evidence-Based Approach</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Solution-focused hypnotherapy is backed by neuroscience research, not "woo-woo" mysticism. Perfect for the health-conscious, educated demographic in Bangor.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Life Transitions Specialist</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Extensive experience with retirement anxiety, empty nest syndrome, and the unique stresses of life's second half.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Convenient Access</h3>
                  <p className="text-gray-700 leading-relaxed">
                    A straightforward journey from Bangor with good transport links - frequent trains on the Belfast-Bangor line and an easy drive. Online sessions also available via Zoom.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default BangorAnxietyStress;
